// ─── Relapse Early Warning System ─────────────────────────────────────────────
//
// Composite risk score based on multiple physiological and behavioral signals.
// Research basis:
//   - JAMA Psychiatry Feb 2026 (n=93, actigraphy): AUC 0.80 for predicting
//     depressive episodes via rest-activity rhythm disruption
//   - npj Digital Medicine 2024: Circadian drift as predictor of mood deterioration
//   - eBioMedicine/Lancet 2024: Causal direction circadian → mood established
//   - Nature Scientific Reports 2021: HRV biofeedback medium effect sizes
//
// Risk factors:
//   1. Circadian drift — sleep midpoint shifting later over 3+ days
//   2. HRV trend — declining HRV vs personal 14-day baseline
//   3. Sleep disruption — efficiency drop, duration changes
//   4. Check-in trajectory — PHQ-4 score trending upward
//   5. Activity drop — significant decrease in daily steps
//   6. Home-bound streak — consecutive days without leaving home
//
// Output: 0–100 risk score with severity level and contributing signals.
// ──────────────────────────────────────────────────────────────────────────────

import { DailyMetrics, PHQ4Entry } from '../types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RelapseSignal {
  key: string;
  label: string;
  description: string;
  score: number;       // 0–100 contribution
  weight: number;      // how much it contributes to final score
  direction: 'risk' | 'protective' | 'neutral';
  value?: string;      // human-readable current value
}

export interface RelapseRisk {
  score: number;             // 0–100 composite
  level: 'low' | 'moderate' | 'elevated' | 'high';
  color: string;
  signals: RelapseSignal[];
  dataQuality: number;       // 0–100, how much data we have
  summary: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function mean(arr: number[]): number {
  if (arr.length === 0) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function stddev(arr: number[]): number {
  if (arr.length < 2) return 0;
  const avg = mean(arr);
  return Math.sqrt(arr.reduce((sum, v) => sum + (v - avg) ** 2, 0) / (arr.length - 1));
}

// Linear regression slope (simple)
function slope(values: number[]): number {
  if (values.length < 2) return 0;
  const n = values.length;
  const xMean = (n - 1) / 2;
  const yMean = mean(values);
  let num = 0, den = 0;
  for (let i = 0; i < n; i++) {
    num += (i - xMean) * (values[i] - yMean);
    den += (i - xMean) ** 2;
  }
  return den === 0 ? 0 : num / den;
}

// Extract bedtime hour (0–24 scale, with late nights wrapping past midnight)
function bedtimeHour(bedtimeStart: string | null): number | null {
  if (!bedtimeStart) return null;
  try {
    const d = new Date(bedtimeStart);
    let h = d.getHours() + d.getMinutes() / 60;
    // If before 6 AM, treat as late night (add 24 for sorting)
    if (h < 6) h += 24;
    return h;
  } catch {
    return null;
  }
}

// ─── Main scoring function ────────────────────────────────────────────────────

export interface HomeLeavingDay {
  date: string;
  leftHome: boolean;
  awayMins: number;
}

export function computeRelapseRisk(
  metrics: DailyMetrics[],
  phq4Entries: PHQ4Entry[],
  homeLeaving?: HomeLeavingDay[],
): RelapseRisk {
  const signals: RelapseSignal[] = [];
  let dataPoints = 0;
  let totalPossible = 6; // 6 signal categories

  // Sort metrics by date ascending
  const sorted = [...metrics].sort((a, b) => a.date.localeCompare(b.date));

  // Use last 14 days as baseline, last 3–7 days as recent
  const last14 = sorted.slice(-14);
  const last7  = sorted.slice(-7);
  const last3  = sorted.slice(-3);

  // ── Signal 1: Circadian Drift (sleep midpoint shifting later) ──────────────
  const bedtimeHours = last7
    .map(m => bedtimeHour(m.bedtime_start))
    .filter((h): h is number => h !== null);

  if (bedtimeHours.length >= 3) {
    dataPoints++;
    const drift = slope(bedtimeHours); // positive = getting later
    const driftMins = drift * 60; // convert to minutes per day

    let circadianScore = 0;
    if (driftMins > 15) circadianScore = 90;       // >15 min/day later = high risk
    else if (driftMins > 10) circadianScore = 70;
    else if (driftMins > 5) circadianScore = 45;
    else if (driftMins > 2) circadianScore = 20;
    else circadianScore = 0;

    signals.push({
      key: 'circadian_drift',
      label: 'Circadian Drift',
      description: driftMins > 2
        ? `Sleep timing shifting ${Math.round(driftMins)} min later each day`
        : 'Sleep timing is stable',
      score: circadianScore,
      weight: 25,
      direction: circadianScore > 30 ? 'risk' : 'protective',
      value: `${driftMins > 0 ? '+' : ''}${Math.round(driftMins)} min/day`,
    });
  }

  // ── Signal 2: HRV Trend vs Personal Baseline ──────────────────────────────
  const hrvValues14 = last14.map(m => m.hrv).filter((v): v is number => v != null && v > 0);
  const hrvValues3 = last3.map(m => m.hrv).filter((v): v is number => v != null && v > 0);

  if (hrvValues14.length >= 5 && hrvValues3.length >= 2) {
    dataPoints++;
    const baseline = mean(hrvValues14);
    const recent = mean(hrvValues3);
    const sd = stddev(hrvValues14);
    const zScore = sd > 0 ? (recent - baseline) / sd : 0;
    const pctChange = baseline > 0 ? ((recent - baseline) / baseline) * 100 : 0;

    let hrvScore = 0;
    if (zScore < -2) hrvScore = 90;            // >2 SD below baseline
    else if (zScore < -1.5) hrvScore = 70;
    else if (zScore < -1) hrvScore = 45;
    else if (zScore < -0.5) hrvScore = 20;
    else hrvScore = 0;

    signals.push({
      key: 'hrv_trend',
      label: 'HRV Trend',
      description: pctChange < -10
        ? `HRV dropped ${Math.abs(Math.round(pctChange))}% vs 14-day baseline`
        : pctChange > 5
        ? `HRV improved ${Math.round(pctChange)}% vs baseline`
        : 'HRV within normal range',
      score: hrvScore,
      weight: 25,
      direction: hrvScore > 30 ? 'risk' : pctChange > 5 ? 'protective' : 'neutral',
      value: `${Math.round(recent)} ms (baseline: ${Math.round(baseline)})`,
    });
  }

  // ── Signal 3: Sleep Disruption ─────────────────────────────────────────────
  const sleepScores14 = last14.map(m => m.sleep_score).filter((v): v is number => v != null);
  const sleepScores3 = last3.map(m => m.sleep_score).filter((v): v is number => v != null);
  const sleepDur7 = last7.map(m => m.total_sleep_mins).filter((v): v is number => v != null);
  const effic7 = last7.map(m => m.sleep_efficiency).filter((v): v is number => v != null);

  if (sleepScores14.length >= 5 && sleepScores3.length >= 2) {
    dataPoints++;
    const baseline = mean(sleepScores14);
    const recent = mean(sleepScores3);
    const dropPct = baseline > 0 ? ((baseline - recent) / baseline) * 100 : 0;

    // Also factor in duration and efficiency
    const avgDur = sleepDur7.length ? mean(sleepDur7) : null;
    const avgEffic = effic7.length ? mean(effic7) : null;
    let durationPenalty = 0;
    if (avgDur != null && avgDur < 300) durationPenalty = 30;      // <5 hrs
    else if (avgDur != null && avgDur < 360) durationPenalty = 15;  // <6 hrs

    let efficPenalty = 0;
    if (avgEffic != null && avgEffic < 70) efficPenalty = 20;
    else if (avgEffic != null && avgEffic < 80) efficPenalty = 10;

    let sleepScore = 0;
    if (dropPct > 20) sleepScore = 80;
    else if (dropPct > 15) sleepScore = 60;
    else if (dropPct > 10) sleepScore = 35;
    else if (dropPct > 5) sleepScore = 15;

    sleepScore = Math.min(100, sleepScore + durationPenalty + efficPenalty);

    signals.push({
      key: 'sleep_disruption',
      label: 'Sleep Quality',
      description: dropPct > 10
        ? `Sleep score dropped ${Math.round(dropPct)}% from baseline`
        : 'Sleep quality is stable',
      score: sleepScore,
      weight: 20,
      direction: sleepScore > 30 ? 'risk' : 'protective',
      value: `${Math.round(recent)} (baseline: ${Math.round(baseline)})`,
    });
  }

  // ── Signal 4: PHQ-4 Trajectory ─────────────────────────────────────────────
  const recentPhq4 = phq4Entries.slice(0, 4); // last 4 weeks, newest first

  if (recentPhq4.length >= 2) {
    dataPoints++;
    const scores = [...recentPhq4].reverse().map(e => e.score); // oldest to newest
    const phq4Slope = slope(scores);
    const latest = recentPhq4[0].score;

    let phq4Score = 0;
    // Severity of current score
    if (latest >= 9) phq4Score = 60;
    else if (latest >= 6) phq4Score = 35;
    else if (latest >= 3) phq4Score = 15;

    // Trajectory adds risk
    if (phq4Slope > 1.5) phq4Score = Math.min(100, phq4Score + 30);
    else if (phq4Slope > 0.75) phq4Score = Math.min(100, phq4Score + 15);
    else if (phq4Slope < -0.75) phq4Score = Math.max(0, phq4Score - 15); // improving

    signals.push({
      key: 'phq4_trajectory',
      label: 'Mood Trajectory',
      description: phq4Slope > 0.75
        ? `PHQ-4 trending upward (${latest}/12)`
        : phq4Slope < -0.75
        ? `PHQ-4 improving (${latest}/12)`
        : `PHQ-4 stable at ${latest}/12`,
      score: phq4Score,
      weight: 20,
      direction: phq4Score > 30 ? 'risk' : phq4Slope < -0.75 ? 'protective' : 'neutral',
      value: `${latest}/12 (trend: ${phq4Slope > 0 ? '+' : ''}${phq4Slope.toFixed(1)}/wk)`,
    });
  }

  // ── Signal 5: Activity Drop ────────────────────────────────────────────────
  const steps14 = last14.map(m => m.steps).filter((v): v is number => v != null);
  const steps3 = last3.map(m => m.steps).filter((v): v is number => v != null);

  if (steps14.length >= 5 && steps3.length >= 2) {
    dataPoints++;
    const baseline = mean(steps14);
    const recent = mean(steps3);
    const dropPct = baseline > 0 ? ((baseline - recent) / baseline) * 100 : 0;

    let actScore = 0;
    if (dropPct > 40) actScore = 80;       // >40% drop
    else if (dropPct > 30) actScore = 55;
    else if (dropPct > 20) actScore = 30;
    else if (dropPct > 10) actScore = 10;

    // Absolute low steps also a risk
    if (recent < 2000) actScore = Math.min(100, actScore + 20);
    else if (recent < 3500) actScore = Math.min(100, actScore + 10);

    signals.push({
      key: 'activity_drop',
      label: 'Activity Level',
      description: dropPct > 20
        ? `Daily steps dropped ${Math.round(dropPct)}% vs baseline`
        : 'Activity levels stable',
      score: actScore,
      weight: 8,
      direction: actScore > 30 ? 'risk' : 'protective',
      value: `${Math.round(recent).toLocaleString()} steps (baseline: ${Math.round(baseline).toLocaleString()})`,
    });
  }

  // ── Signal 6: Home-Bound Streak ─────────────────────────────────────────────
  if (homeLeaving && homeLeaving.length >= 3) {
    dataPoints++;
    // Count consecutive days without leaving home (most recent first)
    const sorted = [...homeLeaving].sort((a, b) => b.date.localeCompare(a.date));
    let streak = 0;
    for (const day of sorted) {
      if (!day.leftHome) streak++;
      else break;
    }

    let homeScore = 0;
    if (streak >= 5) homeScore = 90;         // 5+ days without leaving = high risk
    else if (streak >= 4) homeScore = 70;
    else if (streak >= 3) homeScore = 50;
    else if (streak >= 2) homeScore = 20;
    else homeScore = 0;

    signals.push({
      key: 'home_bound',
      label: 'Home-Leaving',
      description: streak >= 3
        ? `Have not left home in ${streak} consecutive days`
        : streak === 0
        ? 'Left home today'
        : `At home for ${streak} day${streak > 1 ? 's' : ''}`,
      score: homeScore,
      weight: 8,
      direction: homeScore > 30 ? 'risk' : 'protective',
      value: `${streak} day${streak !== 1 ? 's' : ''} home-bound`,
    });
  }

  // ── Compute weighted composite score ────────────────────────────────────────

  const dataQuality = totalPossible > 0 ? Math.round((dataPoints / totalPossible) * 100) : 0;

  let weightedSum = 0;
  let weightTotal = 0;
  for (const s of signals) {
    weightedSum += s.score * s.weight;
    weightTotal += s.weight;
  }
  const compositeScore = weightTotal > 0 ? Math.round(weightedSum / weightTotal) : 0;

  // Determine level
  let level: RelapseRisk['level'];
  let color: string;
  if (compositeScore >= 65) {
    level = 'high';
    color = '#EF4444';
  } else if (compositeScore >= 40) {
    level = 'elevated';
    color = '#F97316';
  } else if (compositeScore >= 20) {
    level = 'moderate';
    color = '#F59E0B';
  } else {
    level = 'low';
    color = '#22C55E';
  }

  // Generate summary
  const riskSignals = signals.filter(s => s.direction === 'risk');
  const protectiveSignals = signals.filter(s => s.direction === 'protective');
  let summary = '';
  if (riskSignals.length === 0) {
    summary = 'All patterns are tracking well — nothing notable to flag.';
  } else if (level === 'high') {
    summary = `A few patterns worth being mindful of: ${riskSignals.map(s => s.label.toLowerCase()).join(', ')}. This might be a good time to check in with yourself or your care team.`;
  } else if (level === 'elevated') {
    summary = `Some shifts worth staying aware of: ${riskSignals.map(s => s.label.toLowerCase()).join(' and ')}.`;
  } else {
    summary = `Mild shifts in ${riskSignals.map(s => s.label.toLowerCase()).join(', ')} — something to stay mindful of.`;
  }

  if (protectiveSignals.length > 0 && riskSignals.length > 0) {
    summary += ` Supporting signals: ${protectiveSignals.map(s => s.label.toLowerCase()).join(', ')}.`;
  }

  return {
    score: compositeScore,
    level,
    color,
    signals,
    dataQuality,
    summary,
  };
}
